import React, { forwardRef, useState, useCallback, useRef, useId } from 'react';
import { UploadCloud, File, X, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Types ─────────────────────────────────────────────────── */
export interface FileItem {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  preview?: string;
}

export interface FileUploadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  accept?: string;
  maxSize?: number;
  multiple?: boolean;
  disabled?: boolean;
  onChange?: (files: File[]) => void;
  onRemove?: (id: string) => void;
}

export const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      className,
      accept,
      maxSize,
      multiple = false,
      disabled = false,
      onChange,
      onRemove,
      ...props
    },
    ref
  ) => {
    const autoId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [files, setFiles] = useState<FileItem[]>([]);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const addFiles = useCallback(
      (incoming: FileList | File[]) => {
        setError(null);
        const arr = Array.from(incoming);

        // Validate type
        if (accept) {
          const accepted = accept.split(',').map((t) => t.trim().toLowerCase());
          const invalid = arr.find(
            (f) =>
              !accepted.some(
                (a) =>
                  a === f.type ||
                  (a.startsWith('.') && f.name.toLowerCase().endsWith(a)) ||
                  (a.endsWith('/*') && f.type.startsWith(a.replace('/*', '/')))
              )
          );
          if (invalid) {
            setError(`File type not allowed: ${invalid.name}`);
            return;
          }
        }

        // Validate size
        if (maxSize) {
          const tooLarge = arr.find((f) => f.size > maxSize);
          if (tooLarge) {
            const mb = (maxSize / 1024 / 1024).toFixed(1);
            setError(`File too large (max ${mb} MB): ${tooLarge.name}`);
            return;
          }
        }

        const items: FileItem[] = arr.map((f) => {
          const id = `${autoId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          return {
            file: f,
            id,
            progress: 0,
            status: 'pending',
            preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
          };
        });

        setFiles((prev) => (multiple ? [...prev, ...items] : items));
        onChange?.(arr);
      },
      [accept, autoId, maxSize, multiple, onChange]
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        addFiles(e.dataTransfer.files);
      },
      [addFiles, disabled]
    );

    const removeFile = useCallback(
      (id: string) => {
        setFiles((prev) => {
          const f = prev.find((i) => i.id === id);
          if (f?.preview) URL.revokeObjectURL(f.preview);
          return prev.filter((i) => i.id !== id);
        });
        onRemove?.(id);
      },
      [onRemove]
    );

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label="Upload files"
          aria-disabled={disabled}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center transition-colors',
            'cursor-pointer',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            dragOver
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
              : 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/30',
            disabled && 'pointer-events-none opacity-50'
          )}
        >
          <UploadCloud
            className={cn(
              'h-10 w-10',
              dragOver ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500'
            )}
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Drop files here or <span className="text-primary-600 dark:text-primary-400">browse</span>
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {accept ? `Accepted: ${accept}` : 'Any file type'}
              {maxSize ? ` · Max ${(maxSize / 1024 / 1024).toFixed(0)} MB` : ''}
            </p>
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            e.target.value = '';
          }}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />

        {error && (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}

        {files.length > 0 && (
          <ul className="mt-3 space-y-2">
            {files.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900"
              >
                {item.preview ? (
                  <img
                    src={item.preview}
                    alt={`Preview of ${item.file.name}`}
                    className="h-10 w-10 shrink-0 rounded-md object-cover"
                  />
                ) : (
                  <File className="h-8 w-8 shrink-0 text-gray-400" aria-hidden="true" />
                )}

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-700 dark:text-gray-300">
                    {item.file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(item.file.size / 1024).toFixed(1)} KB
                  </p>
                  {item.status === 'uploading' && (
                    <div
                      className="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
                      role="progressbar"
                      aria-valuenow={item.progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`Uploading ${item.file.name}`}
                    >
                      <div
                        className="h-full rounded-full bg-primary-500 transition-all"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  )}
                </div>

                {item.status === 'done' && (
                  <CheckCircle className="h-5 w-5 shrink-0 text-green-500" aria-label="Uploaded" />
                )}

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(item.id);
                  }}
                  className={cn(
                    'shrink-0 rounded-md p-1 text-gray-400 hover:text-gray-600',
                    'dark:hover:text-gray-300',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                  )}
                  aria-label={`Remove ${item.file.name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

FileUpload.displayName = 'FileUpload';
